import { createSvgEl, type CartesianDomain } from '@learnforge/demo-render';
import { Fraction } from '@learnforge/demo-math/numeric';
import { nthTerm, partialSum, type SequenceSpec } from '../algebra/sequence';
import { niceTickStep } from './parabola-sketch';

/** Chart labels read as decimals (sliders/readouts already do): ≤2 dp, trimmed. */
export function formatDecimal(f: Fraction): string {
  const rounded = Math.round(f.toNumber() * 100) / 100;
  return Object.is(rounded, -0) ? '0' : rounded.toString();
}

export type SequenceMode = 'arithmetic' | 'geometric' | 'recurrence';

export interface StaircaseOptions {
  mode: SequenceMode;
  a1: Fraction;
  ratioOrDiff: Fraction; // d for arithmetic, r for geometric
  offset?: Fraction; // d for recurrence aₙ₊₁ = ratioOrDiff·aₙ + offset
  steps: number;
  showSigmaStack: boolean;
  gaussPairingOverlay?: boolean;
  highlightStep?: number; // index into the bars (0-based) to highlight
  width: number;
  height: number;
  xDomain?: [number, number];
  yDomain?: [number, number];
}

export interface StaircaseHandle {
  group: SVGGElement;
  update(opts: Partial<StaircaseOptions>): void;
  destroy(): void;
}

let staircaseClipId = 0;

function sequenceSpecFromOptions(
  opts: Pick<StaircaseOptions, 'mode' | 'a1' | 'ratioOrDiff' | 'offset'>,
): SequenceSpec {
  if (opts.mode === 'arithmetic') {
    return { kind: 'arithmetic', a1: opts.a1, d: opts.ratioOrDiff };
  }
  if (opts.mode === 'geometric') {
    return { kind: 'geometric', a1: opts.a1, r: opts.ratioOrDiff };
  }
  return {
    kind: 'recurrence',
    a1: opts.a1,
    c: opts.ratioOrDiff,
    d: opts.offset ?? Fraction.ZERO,
  };
}

export function fitStaircaseDomains(
  opts: Pick<
    StaircaseOptions,
    'mode' | 'a1' | 'ratioOrDiff' | 'offset' | 'steps' | 'showSigmaStack'
  >,
): CartesianDomain {
  const spec = sequenceSpecFromOptions(opts);
  const values = Array.from({ length: opts.steps }, (_, index) =>
    nthTerm(spec, index + 1).toNumber(),
  );
  if (opts.showSigmaStack) {
    values.push(
      ...Array.from({ length: opts.steps }, (_, index) => partialSum(spec, index + 1).toNumber()),
    );
  }
  let yMin = Math.min(0, ...values);
  let yMax = Math.max(0, ...values);
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  } else {
    const padding = (yMax - yMin) * 0.1;
    yMin -= padding;
    yMax += padding;
  }
  return { xDomain: [0.5, opts.steps + 0.5], yDomain: [yMin, yMax] };
}

export function createStaircase(
  parent: SVGSVGElement | SVGGElement,
  opts: StaircaseOptions,
): StaircaseHandle {
  const group = createSvgEl('g', { 'data-role': 'staircase' });
  parent.appendChild(group);

  let currentOpts = { ...opts };

  const render = (renderOpts: StaircaseOptions): void => {
    group.replaceChildren();

    const {
      mode,
      a1,
      ratioOrDiff,
      offset,
      steps,
      showSigmaStack,
      gaussPairingOverlay,
      highlightStep,
      width,
      height,
    } = renderOpts;
    const spec = sequenceSpecFromOptions({ mode, a1, ratioOrDiff, ...(offset ? { offset } : {}) });
    const termValues = Array.from({ length: steps }, (_, index) => nthTerm(spec, index + 1));
    const partialSums = showSigmaStack
      ? Array.from({ length: steps }, (_, index) => partialSum(spec, index + 1))
      : [];
    const fitted = fitStaircaseDomains(renderOpts);
    const xDomain = renderOpts.xDomain ?? fitted.xDomain;
    const yDomain = renderOpts.yDomain ?? fitted.yDomain;
    const mapX = (x: number): number => ((x - xDomain[0]) / (xDomain[1] - xDomain[0])) * width;
    const mapY = (y: number): number =>
      height - ((y - yDomain[0]) / (yDomain[1] - yDomain[0])) * height;

    const defs = createSvgEl('defs');
    const clipId = `alg-staircase-clip-${staircaseClipId++}`;
    const clipPath = createSvgEl('clipPath', { id: clipId });
    clipPath.appendChild(createSvgEl('rect', { x: 0, y: 0, width, height }));
    defs.appendChild(clipPath);
    const content = createSvgEl('g', {
      'clip-path': `url(#${clipId})`,
      'data-role': 'staircase-content',
    });
    group.append(defs, content);

    const xStep = niceTickStep(xDomain[1] - xDomain[0], 8);
    const yStep = niceTickStep(yDomain[1] - yDomain[0], 6);
    const xAxisY = mapY(Math.max(yDomain[0], Math.min(yDomain[1], 0)));
    const yAxisX = mapX(Math.max(xDomain[0], Math.min(xDomain[1], 0)));
    const firstX = Math.ceil(xDomain[0] / xStep);
    for (let index = firstX; index * xStep <= xDomain[1] + xStep * 1e-9; index += 1) {
      const value = index * xStep;
      const x = mapX(value);
      content.appendChild(
        createSvgEl('line', { x1: x, y1: 0, x2: x, y2: height, class: 'alg-gridline' }),
      );
      if (Math.abs(value) <= xStep * 1e-9) continue;
      const label = createSvgEl('text', {
        x,
        y: Math.min(height - 3, xAxisY + 14),
        'text-anchor': 'middle',
        class: 'alg-text-mute',
        'data-axis': 'x',
      });
      label.textContent = formatDecimal(Fraction.from(value));
      content.appendChild(label);
    }
    const firstY = Math.ceil(yDomain[0] / yStep);
    for (let index = firstY; index * yStep <= yDomain[1] + yStep * 1e-9; index += 1) {
      const value = index * yStep;
      const y = mapY(value);
      content.appendChild(
        createSvgEl('line', { x1: 0, y1: y, x2: width, y2: y, class: 'alg-gridline' }),
      );
      if (Math.abs(value) <= yStep * 1e-9) continue;
      const label = createSvgEl('text', {
        x: Math.max(4, yAxisX - 5),
        y: y + 4,
        'text-anchor': yAxisX <= 4 ? 'start' : 'end',
        class: 'alg-text-mute',
        'data-axis': 'y',
      });
      label.textContent = formatDecimal(Fraction.from(value));
      content.appendChild(label);
    }
    content.append(
      createSvgEl('line', {
        x1: 0,
        y1: xAxisY,
        x2: width,
        y2: xAxisY,
        class: 'alg-sketch-axis',
      }),
      createSvgEl('line', {
        x1: yAxisX,
        y1: 0,
        x2: yAxisX,
        y2: height,
        class: 'alg-sketch-axis',
      }),
    );

    const baseY = mapY(0);
    const barWidth = Math.abs(mapX(1.4) - mapX(0.6));
    const barX = (index: number): number => mapX(index + 1 - 0.4);
    const barGeometry = (value: Fraction): { y: number; height: number } => {
      const valueY = mapY(value.toNumber());
      return { y: Math.min(baseY, valueY), height: Math.abs(valueY - baseY) };
    };

    // Render sigma stack bars BEHIND the term bars: same x per column, drawn
    // first so the (now fully-opaque) term bars paint over them cleanly —
    // two translucent fills stacked here used to blend orange+blue into pink.
    if (showSigmaStack) {
      const sigmaGroup = createSvgEl('g', { 'data-role': 'sigma-stack' });

      for (let i = 0; i < steps; i += 1) {
        const geometry = barGeometry(partialSums[i]!);

        const rect = createSvgEl('rect', {
          'data-role': 'bar',
          'data-step': i,
          'data-value': partialSums[i]!.toString(),
          x: barX(i),
          y: geometry.y,
          width: barWidth,
          height: geometry.height,
          class: 'alg-bar-sigma',
        });

        sigmaGroup.appendChild(rect);
      }

      content.appendChild(sigmaGroup);
    }

    // Render main staircase bars
    for (let i = 0; i < steps; i += 1) {
      const geometry = barGeometry(termValues[i]!);

      const rect = createSvgEl('rect', {
        'data-role': 'bar',
        'data-step': i,
        'data-value': termValues[i]!.toString(),
        x: barX(i),
        y: geometry.y,
        width: barWidth,
        height: geometry.height,
        class: highlightStep === i ? 'alg-bar highlighted' : 'alg-bar',
      });

      content.appendChild(rect);
    }

    // Where the Sₙ total label will land on the last column (if shown), so
    // the term-value label on that same column can dodge it.
    let sigmaTotalLabelY: number | null = null;
    if (showSigmaStack) {
      const lastSigmaTopY = barGeometry(partialSums[steps - 1]!).y;
      sigmaTotalLabelY = Math.max(lastSigmaTopY - 4, 8);
    }

    // Term value labels
    for (let i = 0; i < steps; i += 1) {
      const barTopY = barGeometry(termValues[i]!).y;
      const termLabelY = Math.max(barTopY - 4, 8);

      if (
        showSigmaStack &&
        i === steps - 1 &&
        sigmaTotalLabelY !== null &&
        Math.abs(termLabelY - sigmaTotalLabelY) < 12
      ) {
        continue; // would collide with the sigma-total label — skip
      }

      const label = createSvgEl('text', {
        'data-role': 'bar-value',
        x: barX(i) + barWidth / 2,
        y: termLabelY,
        'text-anchor': 'middle',
        class: 'alg-text-mute',
      });
      label.textContent = formatDecimal(termValues[i]!);
      content.appendChild(label);
    }

    // Sₙ label above the last sigma column (drawn last, alongside the other
    // value labels, so it sits on top of both bar layers)
    if (showSigmaStack) {
      const lastX = barX(steps - 1);
      const lastTopY = barGeometry(partialSums[steps - 1]!).y;
      const totalLabel = createSvgEl('text', {
        'data-role': 'sigma-total',
        x: lastX + barWidth / 2,
        y: Math.max(lastTopY - 4, 8),
        'text-anchor': 'middle',
        class: 'alg-text-mute',
      });
      totalLabel.textContent = formatDecimal(partialSums[steps - 1]!);
      content.appendChild(totalLabel);
    }

    // Render Gauss pairing overlay if requested
    if (gaussPairingOverlay && mode === 'arithmetic') {
      const gaussGroup = createSvgEl('g', { 'data-role': 'gauss-overlay' });

      // Create reversed staircase (flip the order of bars)
      for (let i = 0; i < steps; i += 1) {
        const reversedIndex = steps - 1 - i;
        const geometry = barGeometry(termValues[reversedIndex]!);

        const rect = createSvgEl('rect', {
          'data-role': 'bar',
          'data-step': reversedIndex,
          'data-value': termValues[reversedIndex]!.toString(),
          x: barX(i),
          y: geometry.y,
          width: barWidth,
          height: geometry.height,
          class: 'alg-bar-overlay',
        });

        gaussGroup.appendChild(rect);
      }

      content.appendChild(gaussGroup);
    }
  };

  render(currentOpts);

  return {
    group,
    update(opts: Partial<StaircaseOptions>): void {
      currentOpts = { ...currentOpts, ...opts };
      render(currentOpts);
    },
    destroy(): void {
      if (group.parentNode) {
        group.parentNode.removeChild(group);
      }
    },
  } satisfies StaircaseHandle;
}
